import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrgStore } from '@/stores/useOrgStore';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency, formatNumber } from '@/lib/format';
import { FolderOpen, Users, TrendingUp, Activity } from 'lucide-react';
import { PortfolioPerformanceChart } from '@/components/dashboard/PortfolioPerformanceChart';
import { CaseStatusChart } from '@/components/dashboard/CaseStatusChart';
import { RecoveryTrendChart } from '@/components/dashboard/RecoveryTrendChart';
import { AccountAgingChart } from '@/components/dashboard/AccountAgingChart';

const Dashboard = () => {
  const { activeOrg } = useOrgStore();
  const [stats, setStats] = useState({
    portfolioCount: 0,
    totalNominal: 0,
    accountCount: 0,
    activeCases: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeOrg) return;

    const loadStats = async () => {
      setLoading(true);
      try {
        // TODO: Load via edge functions
        const portfolios: any[] = [];
        const casesCount = 0;

        setStats({
          portfolioCount: portfolios?.length || 0,
          totalNominal: portfolios?.reduce((sum, p) => sum + (p.nominal_value || 0), 0) || 0,
          accountCount: portfolios?.reduce((sum, p) => sum + (p.account_count || 0), 0) || 0,
          activeCases: casesCount || 0,
        });
      } catch (error) {
        console.error('Error loading dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [activeOrg]);

  const statCards = [
    {
      title: 'Total Portfolios',
      value: formatNumber(stats.portfolioCount),
      description: 'Active debt portfolios',
      icon: FolderOpen,
    },
    {
      title: 'Total Nominal Value',
      value: formatCurrency(stats.totalNominal),
      description: 'Across all portfolios',
      icon: TrendingUp,
    },
    {
      title: 'Total Accounts',
      value: formatNumber(stats.accountCount),
      description: 'Debt accounts',
      icon: Users,
    },
    {
      title: 'Active Cases',
      value: formatNumber(stats.activeCases),
      description: 'In CRM workflow',
      icon: Activity,
    },
  ];

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of your debt portfolio management
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <PortfolioPerformanceChart />
          <CaseStatusChart />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <RecoveryTrendChart />
          <AccountAgingChart />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Welcome to DebtNet</CardTitle>
            <CardDescription>
              Begin by uploading your first debt portfolio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2 text-sm">
                <p>1. Navigate to <strong>Upload</strong> to import your debt portfolio CSV/Excel file</p>
                <p>2. Map columns to required fields (ID number, principal, balance, etc.)</p>
                <p>3. Validate and create your portfolio</p>
                <p>4. Run valuation to get IRR, NPV, and suggested pricing</p>
                <p>5. Manage cases through the CRM workflow</p>
              </div>
              <Button variant="outline" onClick={() => window.location.href = '/portfolios'}>
                View Portfolios
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
